import { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/Store';
import { addUsuarioEmpresa, getEmpresas, getEmpresasByUsuario, getUsuarios, removeUsuarioEmpresa } from 'actions/UsuariosEmpresas/usuariosEmpresas';

interface Usuario { id: number; nombre: string; email: string; tipo_usuario_id: number; empresa_id: number | null }
interface Empresa { id: number; nombre: string }

const UsuariosEmpresas = () => {
  const auth = useSelector((s: RootStore) => s.auth);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarioSel, setUsuarioSel] = useState<Usuario | null>(null);
  const [empresasAsignadas, setEmpresasAsignadas] = useState<number[]>([]);
  const [empresaFiltro, setEmpresaFiltro] = useState<string>("");
  const isSuperadmin = auth.tipo_usuario_id === 1;

  useEffect(() => {
    if (!isSuperadmin) return;
    getUsuarios(500)
      .then(r => setUsuarios(r.data.data || []))
      .catch(() => setUsuarios([]));
    getEmpresas()
      .then(r => setEmpresas(r.data || []))
      .catch(() => setEmpresas([]));
  }, [isSuperadmin]);

  useEffect(() => {
    if (!usuarioSel) return;
    getEmpresasByUsuario(usuarioSel.id)
      .then(r => setEmpresasAsignadas((r.data as Empresa[]).map(e => e.id)))
      .catch(() => setEmpresasAsignadas([]));
  }, [usuarioSel]);

  const handleToggle = async (empresaId: number, checked: boolean) => {
    if (!usuarioSel) return;
    try {
      if (checked) {
        await addUsuarioEmpresa(usuarioSel.id, empresaId);
        setEmpresasAsignadas(prev => [...new Set([...prev, empresaId])]);
      } else {
        await removeUsuarioEmpresa(usuarioSel.id, empresaId);
        setEmpresasAsignadas(prev => prev.filter(id => id !== empresaId));
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Operación no permitida');
    }
  };

  const empresasMap = useMemo(() => new Set(empresasAsignadas), [empresasAsignadas]);
  const empresasVisibles = useMemo(() => {
    const q = empresaFiltro.trim().toLowerCase();
    if (!q) return empresas;
    return empresas.filter((e) => e.nombre.toLowerCase().includes(q) || String(e.id).includes(q));
  }, [empresas, empresaFiltro]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {!isSuperadmin ? (
          <Card style={{ padding: 16 }}>
            <MDTypography variant='h5'>No autorizado</MDTypography>
            <MDTypography variant='body2'>Solo superadmin puede asignar empresas a usuarios.</MDTypography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card style={{ padding: 16 }}>
                <MDTypography variant='h6'>Selecciona usuario</MDTypography>
                <Autocomplete<Usuario, false, false, false>
                  options={usuarios}
                  value={usuarioSel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(u) => (u ? `${u.nombre} <${u.email}>` : '')}
                  onChange={(_, v) => setUsuarioSel(v)}
                  renderInput={(params) => <TextField {...params} label='Usuario' />}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card style={{ padding: 16, minHeight: 200 }}>
                <MDTypography variant='h6'>Empresas</MDTypography>
                <MDBox mt={1} mb={1}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Buscar empresa'
                    placeholder='Nombre o ID'
                    value={empresaFiltro}
                    onChange={(e) => setEmpresaFiltro(e.target.value)}
                  />
                </MDBox>
                {empresasVisibles.map((e) => (
                  <MDBox key={e.id} display='flex' alignItems='center' justifyContent='space-between' py={1}>
                    <Typography>{e.nombre}</Typography>
                    <Switch
                      checked={empresasMap.has(e.id)}
                      onChange={(_, checked) => handleToggle(e.id, checked)}
                      disabled={!usuarioSel}
                    />
                  </MDBox>
                ))}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default UsuariosEmpresas;
